import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    LookupItem,
    Logo,
    StationItem,
    ChannelItem,
    SegmentItem,
    Segment,
    PricingTier,
    StringLookupItem,
    Period,
    ApiPeriod,
    SavedFilter,
    BooleanLookupItem,
    DOWLookupItem,
    CustomDaypart,
    HourLookupItem,
    DaypartLookupItem,
    AdvertiserInsightsFilterParams,
    MarketItem,
    Grid,
    RateExpirationDate,
    PoliticalWindowItem,
    PoliticalTierItem,
    ModuleConfig,
    StationExceptionRow,
    Quarter,
    PoliticalInitializationTiers,
    RevenueCodeItem,
    OrderTypeItem,
    HttpParams,
    SavedReport,
    RateCardPeriod,
    PoliticalInitializationTiersConfiguration,
    PcodePrimaryTierMap,
    SegmentDOWLookupItem,
    ReportAccess,
    PriorityCodeTiersQuarter,
    ReportUser,
    ChannelGroupItem,
    HIPCustomGroupItem,
    SpecialEventItem,
} from '@models/lookup';
import { forkJoin, Observable, of } from 'rxjs';
import {
    AsOfDate,
    EditSegmentNameParams,
    MetricsOrderTypedItem,
    SystemSegment,
    HiatusWeekSelection,
    RateCardMetricAlertCalculations,
    DynamicRateCardApiGrid,
    RateCardMetricRowV2,
    DynamicRateCardMetricValueV2,
    RateCardPricingTier,
} from '@models/rate-card/rate-card';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { Segmentation } from '@models/config';
import { merge } from 'lodash';
import { UrlStore } from '@shared/helpers/constants/url-store';
import { TenantAwareStorageService } from '@shared/services/tenant-aware-storage/tenant-aware-storage.service';
import { generateCachedGet } from '@shared/helpers/functions/helpers';
import { calculateRateCardAlerts2, dynamicRateCardCopyCell } from '@shared/helpers/functions/rate_card_helpers';
import { FilterSettings } from '@models/filter';
import { GlobalCustomDayPartRow, PoliticalWindowRow } from '@models/admin/admin';
import moment, { Moment } from 'moment';
import { ExistingReportRowData } from '@models/high-impact-program-report/high-impact-program-report';

@Injectable({
    providedIn: 'root',
})
export class LookupV2Service {
    cacheData: { [url: string]: Observable<unknown> } = {};

    constructor(
        private http: HttpClient,
        private httpBackend: HttpBackend,
        private storage: TenantAwareStorageService,
    ) {}

    getPeriods(
        screenName: string = UrlStore.screenNames.rateCard,
        tab: number | null = null,
        channelGroup: number | null = null,
        clearCache: boolean = false,
    ): Observable<Period[]> {
        let periodsUrl = UrlStore.api.lookupsV2.period + `?screenName=${screenName}`;
        if (tab) {
            periodsUrl += `&tab=${tab}`;
        }
        if (channelGroup) {
            periodsUrl += `&channelGroup=${channelGroup}`;
        }

        return this.generateCachedGet<{ id: number; name: string; startDates: string[]; endDates: string[] }>(clearCache, periodsUrl).pipe(
            // @ts-expect-error type mismatch
            switchMap((periods: ApiPeriod[]) => {
                const returnPeriods: Period[] = [];
                periods.forEach((period) => {
                    period.startDates.sort((a, b) => (a > b ? 1 : -1));
                    period.endDates.sort((a, b) => (a > b ? 1 : -1));
                    const mappedPeriod: Period = {
                        id: period.id,
                        name: period.name,
                        startDateMap: {},
                        endDateMap: {},
                        startDates: period.startDates,
                        endDates: period.endDates,
                        maxQueryCount: period.maxQueryCount,
                        averageDaysPer: period.averageDaysPer,
                        isPolitical: period.isPolitical,
                        isDefault: period.isDefault,
                    };
                    period.startDates.forEach((date) => {
                        const split = date.split('-');
                        const temp2 = { [Number(split[0])]: { [Number(split[1])]: { [Number(split[2])]: date } } };
                        mappedPeriod.startDateMap = merge(mappedPeriod.startDateMap, temp2);
                    });
                    period.endDates.forEach((date) => {
                        const split = date.split('-');
                        const temp2 = { [Number(split[0])]: { [Number(split[1])]: { [Number(split[2])]: date } } };
                        mappedPeriod.endDateMap = merge(mappedPeriod.endDateMap, temp2);
                    });

                    returnPeriods.push(mappedPeriod);
                });
                return of(returnPeriods);
            }),
        );
    }

    getAdvertiserInsightsAdvertisers(
        filterParams: AdvertiserInsightsFilterParams,
        clearCache: boolean = false,
    ): Observable<StringLookupItem[]> {
        return this.generateCachedGet<StringLookupItem>(
            clearCache,
            UrlStore.api.advertiserInsightsLookups.advertisers,
            filterParams,
            false,
        );
    }

    getAdvertiserInsightsAdvertisersCount(filterParams?: AdvertiserInsightsFilterParams, clearCache: boolean = false): Observable<number> {
        return this.generateCachedGetNoArray<number>(
            clearCache,
            UrlStore.api.advertiserInsightsLookups.advertisersCount,
            filterParams,
            false,
        );
    }

    getAdvertiserInsightsAgencies(
        filterParams: AdvertiserInsightsFilterParams,
        clearCache: boolean = false,
    ): Observable<StringLookupItem[]> {
        return this.generateCachedGet<StringLookupItem>(clearCache, UrlStore.api.advertiserInsightsLookups.agencies, filterParams, false);
    }

    getNoChargeOptions(): Observable<LookupItem[]> {
        return of([
            { id: 1, name: 'Include on Separate Row' },
            { id: 2, name: 'Include' },
            { id: 3, name: 'Exclude' },
        ]);
    }

    getOrderDetailOptions(): Observable<LookupItem[]> {
        return this.http.get<LookupItem[]>(UrlStore.api.advertiserInsightsLookups.orderDetailOptions);
    }

    getPricingTiers(channelIds: number[] = null, clearCache: boolean = false, channelGroup?: number): Observable<PricingTier[]> {
        const params: string[] = [];
        if (channelIds) {
            params.push(`channels=${channelIds.join(',')}`);
        }
        if (channelGroup) {
            params.push(`channelGroup=${channelGroup}`);
        }
        const callUrl = params.length
            ? UrlStore.api.lookupsV2.pricingTiers.concat(`?${params.join('&')}`)
            : UrlStore.api.lookupsV2.pricingTiers;
        return this.generateCachedGet<PricingTier>(clearCache, callUrl);
    }

    getDaysOfWeek(clearCache: boolean = false): Observable<BooleanLookupItem[]> {
        return this.generateCachedGet<BooleanLookupItem>(clearCache, UrlStore.api.lookupsV2.dow);
    }

    getRateCardSpecialEventTypes(daypartIds: number[], spotTypeIds: number[]): Observable<SpecialEventItem[]> {
        const url = UrlStore.api.lookupsV2.specialEventTypes
            .concat(`?daypartIds=${daypartIds.join(',')}`)
            .concat(`&spotTypeIds=${spotTypeIds}`);
        return this.http.get<SpecialEventItem[]>(url);
    }

    getRateCardPeriods(date: string): Observable<RateCardPeriod[]> {
        return this.http.get<RateCardPeriod[]>(UrlStore.api.lookupsV2.rateCardPeriods + `?date=${date}`);
    }

    createSpecialEventType(type: string): Observable<{ isNew: boolean; item: SpecialEventItem }> {
        const body = { specialEventType: type };
        return this.http.post<{
            isNew: boolean;
            item: SpecialEventItem;
        }>(UrlStore.api.specialEventsV2.specialEventType, body);
    }

    updateRateCardSpecialEventType(cardId: string, eventId: number) {
        let body = {
            rateCardId: cardId,
        };
        if (eventId) {
            body = { ...body, ...{ specialEventId: eventId } };
        }
        return this.http.put(UrlStore.api.specialEventsV2.specialEventType, body);
    }

    getPriorityCodeTiers(channelIds: number[]): Observable<PcodePrimaryTierMap[]> {
        const url = UrlStore.api.rateCardLookupsV2.priorityCodeTiers.concat(`?channels=${channelIds.join(',')}`);
        return this.generateCachedGet<PcodePrimaryTierMap>(false, url);
    }

    getPriorityCodeTiersQuarters(channelIds: number[]): Observable<PriorityCodeTiersQuarter[]> {
        const url = UrlStore.api.admin.priorityCodeTiersQuarters.concat(`?channels=${channelIds.join(',')}`);
        return this.generateCachedGet<PriorityCodeTiersQuarter>(false, url);
    }

    getRateCardQuarters(checkAvailableRates = false, checkAvailablePcodeRates = false, channelIds: number[] = []): Observable<Quarter[]> {
        const url = UrlStore.api.rateCardLookupsV2.quarters
            .concat(`?checkAvailableRates=${checkAvailableRates}`)
            .concat(`&checkAvailablePcodeRates=${checkAvailablePcodeRates}`)
            .concat(channelIds.length > 0 ? `&channels=${channelIds.join(',')}` : '');
        return this.generateCachedGet<Quarter>(false, url);
    }

    getRateCardSegments(
        channelIds: number[],
        daypartIds: number[],
        segmentation?: Segmentation,
        filterValues: { [k: string]: (string | number)[] } = {},
        clearCache = false,
    ): Observable<SegmentItem[]> {
        return this.getSegments(channelIds, daypartIds, segmentation, filterValues, clearCache, UrlStore.api.rateCardLookupsV2.segments);
    }

    getRateCardAddRowPopupSegments(
        channelIds: number[],
        daypartIds: number[],
        segmentation?: Segmentation,
        filterValues: { [k: string]: (string | number)[] } = {},
        clearCache = false,
    ): Observable<SegmentItem[]> {
        return this.getSegments(
            channelIds,
            daypartIds,
            segmentation,
            filterValues,
            clearCache,
            UrlStore.api.rateCardLookupsV2.addRowPopupSegments,
        );
    }

    getPoliticalRateCardSegments(
        channelIds: number[],
        daypartIds: number[],
        segmentation?: Segmentation,
        filterValues: { [k: string]: (string | number)[] } = {},
        clearCache = false,
    ): Observable<SegmentItem[]> {
        return this.getSegments(
            channelIds,
            daypartIds,
            segmentation,
            filterValues,
            clearCache,
            UrlStore.api.rateCardLookupsV2.politicalSegments,
        );
    }

    getDigitalSegments(
        channelIds: number[],
        daypartIds: number[],
        segmentation?: Segmentation,
        filterValues: { [k: string]: (string | number)[] } = {},
        clearCache = false,
    ): Observable<SegmentItem[]> {
        return this.getSegments(channelIds, daypartIds, segmentation, filterValues, clearCache, UrlStore.api.digital.segments);
    }

    getSpecialEventSegments(
        channelIds: number[],
        daypartIds: number[],
        segmentation?: Segmentation,
        filterValues: { [k: string]: (string | number)[] } = {},
        clearCache = false,
    ): Observable<SegmentItem[]> {
        return this.getSegments(channelIds, daypartIds, segmentation, filterValues, clearCache, UrlStore.api.specialEventsV2.segments);
    }

    getPoliticalLurSegments(
        channelIds: number[],
        daypartIds: number[],
        segmentation?: Segmentation,
        filterValues: { [k: string]: (string | number)[] } = {},
        clearCache = false,
    ): Observable<SegmentItem[]> {
        return this.getSegments(channelIds, daypartIds, segmentation, filterValues, clearCache, UrlStore.api.politicalLURV2.segments);
    }

    getInventoryInsightsSegments(
        channelIds: number[],
        daypartIds: number[],
        segmentation?: Segmentation,
        filterValues: { [k: string]: (string | number)[] } = {},
        clearCache = false,
    ): Observable<SegmentItem[]> {
        return this.getSegments(
            channelIds,
            daypartIds,
            segmentation,
            filterValues,
            clearCache,
            UrlStore.api.inventoryInsightsLookups.segments,
        );
    }

    getDashboardScenarioSegments(
        channelIds: number[],
        revenueCategoryIds: number[],
        segmentTye?: string,
        clearCache = false,
    ): Observable<SegmentItem[]> {
        return this.getScenarioSegments(channelIds, revenueCategoryIds, segmentTye, clearCache, UrlStore.api.rateCardLookupsV2.segments);
    }

    getRateGenSpotLengths(channelGroup: number | undefined, spotTypeIDs: number[]): Observable<LookupItem[]> {
        let url = UrlStore.api.rateGen.spotLengths;
        const params: string[] = [];
        if (channelGroup) {
            params.push(`channelGroup=${channelGroup}`);
        }
        if (spotTypeIDs.length) {
            params.push(`spotType=${spotTypeIDs.join(',')}`);
        }
        if (params.length) {
            url += '?' + params.join('&');
        }
        return this.http.get<LookupItem[]>(url);
    }

    getPoliticalRateCardSpotLengths(clearCache: boolean = false): Observable<LookupItem[]> {
        const url = UrlStore.api.rateCard.politicalRateCardSpotLengths;
        return this.getSpotLengths(clearCache, url);
    }

    getPoliticalLurSpotLengths(clearCache: boolean = false): Observable<LookupItem[]> {
        return this.getSpotLengths(clearCache, UrlStore.api.politicalLURV2.spotLengths);
    }

    getChannelGroups(
        clearCache = false,
        hasDynamicRates = false,
        hasPoliticalRates = false,
        hasDigitalRates = false,
        tabNumber: number = null,
    ): Observable<ChannelGroupItem[]> {
        let url = UrlStore.api.lookupsV2.channelGroups;
        if (hasDynamicRates) {
            url += '?hasDynamicRates=true';
            if (tabNumber) {
                url += `&tabNumber=${tabNumber}`;
            }
        } else if (hasPoliticalRates) {
            url += '?hasPoliticalRates=true';
        } else if (hasDigitalRates) {
            url += '?hasDigitalRates=true';
        }
        return this.generateCachedGet<ChannelGroupItem>(clearCache, url);
    }

    getMarkets(
        clearCache = false,
        hasDynamicRates = false,
        hasPoliticalRates = false,
        hasDigitalRates = false,
        tabNumber: number = null,
    ): Observable<MarketItem[]> {
        let url = UrlStore.api.lookupsV2.markets;
        if (hasDynamicRates) {
            url += '?hasDynamicRates=true';
            if (tabNumber) {
                url += `&tabNumber=${tabNumber}`;
            }
        } else if (hasPoliticalRates) {
            url += '?hasPoliticalRates=true';
        } else if (hasDigitalRates) {
            url += '?hasDigitalRates=true';
        }
        return this.generateCachedGet<MarketItem>(clearCache, url);
    }

    getPCodeMarkets(clearCache = false, hasDynamicRates = false): Observable<MarketItem[]> {
        let url = UrlStore.api.lookupsV2.pCodeMarkets;
        if (hasDynamicRates) {
            url += '?hasDynamicRates=true';
        }
        return this.generateCachedGet<MarketItem>(clearCache, url);
    }

    getPCodeStations(clearCache = false, hasDynamicRates = false): Observable<StationItem[]> {
        let url = UrlStore.api.lookupsV2.pCodeStations;
        if (hasDynamicRates) {
            url += '?hasDynamicRates=true';
        }
        return this.generateCachedGet<StationItem>(clearCache, url);
    }

    getPCodeChannels(clearCache = false, hasDynamicRates = false): Observable<ChannelItem[]> {
        let url = UrlStore.api.lookupsV2.pCodeChannels;
        if (hasDynamicRates) {
            url += '?hasDynamicRates=true';
        }
        return this.generateCachedGet<ChannelItem>(clearCache, url);
    }

    getStations(
        clearCache = false,
        hasDynamicRates = false,
        hasPoliticalRates = false,
        hasDigitalRates = false,
        tabNumber: number = null,
    ): Observable<StationItem[]> {
        let url = UrlStore.api.lookupsV2.stations;
        if (hasDynamicRates) {
            url += '?hasDynamicRates=true';
            if (tabNumber) {
                url += `&tabNumber=${tabNumber}`;
            }
        } else if (hasPoliticalRates) {
            url += '?hasPoliticalRates=true';
        } else if (hasDigitalRates) {
            url += '?hasDigitalRates=true';
        }
        return this.generateCachedGet<StationItem>(clearCache, url);
    }

    getChannels(
        clearCache = false,
        hasDynamicRates = false,
        hasPoliticalRates = false,
        hasDigitalRates = false,
        tabNumber: number = null,
        hasDashboardData = false,
    ): Observable<ChannelItem[]> {
        let url = UrlStore.api.lookupsV2.channels;
        if (hasDynamicRates) {
            url += '?hasDynamicRates=true';
            if (tabNumber) {
                url += `&tabNumber=${tabNumber}`;
            }
        } else if (hasPoliticalRates) {
            url += '?hasPoliticalRates=true';
        } else if (hasDigitalRates) {
            url += '?hasDigitalRates=true';
        } else if (hasDashboardData) {
            url += '?hasDashboardData=true';
        }
        return this.generateCachedGet<ChannelItem>(clearCache, url);
    }

    getPriorityCodes(channelGroup: number | undefined): Observable<LookupItem[]> {
        let url = UrlStore.api.lookupsV2.priorityCodes;
        if (channelGroup) {
            url += `?channelGroup=${channelGroup}`;
        }
        return this.http.get<LookupItem[]>(url);
    }

    getDayparts(
        channelIds: number[],
        spotTypeIds: number[],
        filterSettings: FilterSettings,
        clearCache: boolean = false,
    ): Observable<DaypartLookupItem[]> {
        let reqUrl = UrlStore.api.lookupsV2.dayparts;
        reqUrl = reqUrl.concat(`?channelIds=${channelIds.join(',')}`);

        if (filterSettings.useCommonDayparts) {
            reqUrl += `&readCommonDayparts=true`;
        }
        if (spotTypeIds.length > 0) {
            reqUrl += `&spotTypes=${spotTypeIds.join(',')}`;
        }
        if (filterSettings.hasDynamicRates) {
            reqUrl += '&hasDynamicRates=true';
        } else if (filterSettings.hasPoliticalRates) {
            reqUrl += '&hasPoliticalRates=true';
        } else if (filterSettings.hasDigitalRates) {
            reqUrl += '&hasDigitalRates=true';
        } else if (filterSettings.hasLURData) {
            reqUrl += '&hasLURData=true';
        }
        // the tab and which half of it is shown decide whether its sports dayparts belong
        if (filterSettings.tabNumber && (filterSettings.hasDynamicRates || filterSettings.hasPoliticalRates)) {
            reqUrl += `&tab=${filterSettings.tabNumber}`;
        }
        if (filterSettings.isSportView) {
            reqUrl += '&isSportView=true';
        }
        if (filterSettings.includeCustomDayparts) {
            reqUrl += '&includeCustom=true';
        }
        if (filterSettings.useCanonicalName) {
            reqUrl += '&useCanonicalName=true';
        }
        if (filterSettings.isRateGen) {
            reqUrl += '&isRateGen=true';
        }
        if (filterSettings.hasInventoryInsightsData) {
            reqUrl += '&hasInventoryInsightsData=true';
        }
        if (filterSettings.hasAdvertiserInsightsData) {
            reqUrl += '&hasAdvertiserInsightsData=true';
        }
        return this.generateCachedGet<DaypartLookupItem>(clearCache, reqUrl);
    }

    addCustomDaypart(newCustomDaypart: CustomDaypart): Observable<CustomDaypart> {
        return this.http.put<CustomDaypart>(UrlStore.api.customDaypart.customDaypart, newCustomDaypart);
    }

    getCustomDaypartsOutofAllDayparts(dayparts: string): Observable<{ id: number }[]> {
        return this.http.get<{ id: number }[]>(UrlStore.api.customDaypart.customDaypart + `?dayparts=${dayparts}`);
    }

    patchCustomDaypartUserAccess(dayparts: string): Observable<unknown> {
        return this.http.patch<CustomDaypart>(UrlStore.api.customDaypart.customDaypart + `?dayparts=${dayparts}`, {});
    }

    getCustomDaypart(): Observable<Grid<GlobalCustomDayPartRow>> {
        return this.http.get<Grid<GlobalCustomDayPartRow>>(UrlStore.api.customDaypart.customDaypartAll);
    }

    deleteCustomDaypartById(customDaypartId: unknown): Observable<unknown> {
        return this.http.delete<CustomDaypart>(UrlStore.api.customDaypart.customDaypart + `/${customDaypartId}`);
    }

    deleteCustomDaypartAccessForUser(customDaypartId: number): Observable<CustomDaypart> {
        return this.http.delete<CustomDaypart>(UrlStore.api.customDaypart.customDaypartAccessForUser + `/${customDaypartId}`);
    }

    getSpotTypes(channelGroup: number | undefined, channelIds: number[], filterSettings: FilterSettings): Observable<LookupItem[]> {
        let url = UrlStore.api.lookupsV2.spotTypes;
        const params: string[] = [];

        if (filterSettings.hasDynamicRates) {
            params.push('hasDynamicRates=true');
            if (filterSettings.tabNumber) {
                params.push(`tab=${filterSettings.tabNumber}`);
            }
        } else if (filterSettings.hasPoliticalRates) {
            params.push('hasPoliticalRates=true');
        } else if (filterSettings.hasDigitalRates) {
            params.push('hasDigitalRates=true');
        }
        if (filterSettings.isRateGen) {
            params.push('isRateGen=true');
        }
        if (channelGroup) {
            params.push(`channelGroup=${channelGroup}`);
        }
        if (channelIds.length) {
            params.push(`channels=${channelIds.join(',')}`);
        }
        if (params.length) {
            url += '?' + params.join('&');
        }
        return this.generateCachedGet<LookupItem>(false, url);
    }

    getAsOfDates(): Observable<AsOfDate> {
        return generateCachedGet<AsOfDate>(false, UrlStore.api.lookupsV2.asOfDatesV2, this.http, undefined, this.cacheData, false);
    }

    getRateCardModuleConfig(module: string): Observable<ModuleConfig[]> {
        return this.generateCachedGet<ModuleConfig>(false, UrlStore.api.rateCardLookupsV2.moduleConfig + `?module=${module}`);
    }

    getPoliticalTiers(channelIds?: number[], clearCache: boolean = false): Observable<LookupItem[]> {
        let url = UrlStore.api.admin.politicalTiers;
        if (channelIds?.length > 0) {
            url += `?channels=${channelIds.join(',')}`;
        }

        return generateCachedGet(clearCache, url, this.http, undefined, this.cacheData);
    }

    getPoliticalBoundOptions(): Observable<PoliticalTierItem[]> {
        return this.http.get<PoliticalTierItem[]>(UrlStore.api.internalAdmin.politicalBoundConfigOptions);
    }

    getRevenueCodes(): Observable<RevenueCodeItem[]> {
        return this.http.get<RevenueCodeItem[]>(UrlStore.api.admin.revenueCodes);
    }

    getOrderType(): Observable<OrderTypeItem[]> {
        return this.http.get<OrderTypeItem[]>(UrlStore.api.admin.orderType);
    }

    getPoliticalWindows(
        marketIds: number[],
        includeInitStrategy: boolean = false,
        removePastWindows: boolean = false,
    ): Observable<PoliticalWindowItem[]> {
        return this.http.get<PoliticalWindowItem[]>(
            UrlStore.api.lookupsV2.politicalWindow +
                `?marketIds=${marketIds}&includeInitStrategy=${includeInitStrategy}&removePastWindows=${removePastWindows}`,
        );
    }

    getAllPoliticalWindows(): Observable<PoliticalWindowItem[]> {
        return this.http.get<PoliticalWindowItem[]>(UrlStore.api.lookupsV2.allPoliticalWindow);
    }

    getPoliticalInitAllTiers(channelIds: number[] = []): Observable<PoliticalInitializationTiers[]> {
        return this.http.get<PoliticalInitializationTiers[]>(UrlStore.api.lookupsV2.politicalInitAllTiers + `?channelIds=${channelIds}`);
    }

    getPoliticalInitPoliticalTiers(
        channelIds: number[] = [],
        politicalWindowIds: number[] = [],
    ): Observable<PoliticalInitializationTiersConfiguration[]> {
        const url = UrlStore.api.lookupsV2.politicalInitPoliticalTiers + `?channelIds=${channelIds}&politicalWindows=${politicalWindowIds}`;
        return this.http.get<PoliticalInitializationTiersConfiguration[]>(url);
    }

    generateCachedGet<T>(clearCache: boolean, url: string, params?: HttpParams, useRequestData = true): Observable<T[]> {
        return generateCachedGet<T[]>(clearCache, url, this.http, params, this.cacheData, useRequestData);
    }

    generateCachedGetNoArray<T>(clearCache: boolean, url: string, params?: HttpParams, useRequestData = true): Observable<T> {
        return generateCachedGet<T>(clearCache, url, this.http, params, this.cacheData, useRequestData);
    }

    saveReport(
        screenName: string,
        tabData: number,
        reportData: unknown,
        filter: unknown,
        channels: string,
        reportName: string,
        isNewReport: boolean,
        reportId: number,
        updateFiltersOnly: boolean = false,
        isPreview: boolean = false,
    ): Observable<number> {
        const url = UrlStore.api.lookupsV2.savedReport.concat(`/${screenName}`);
        let body = {
            is_new_report: isNewReport,
            report_name: reportName,
            screen_name: screenName,
            tab: tabData,
            data: reportData,
            filter,
            channels,
            update_filters_only: updateFiltersOnly,
            is_preview: isPreview,
        };
        if (reportId) {
            body = { ...body, ...{ report_id: reportId } };
        }
        return this.http.put<number>(url, body);
    }

    saveHipReportWithAIGroups(
        reportData: unknown,
        filter: unknown,
        channels: string,
        reportName: string | null,
        isNewReport: boolean,
        reportId: number | null,
        isPreview: boolean,
        aiGeneratedGroups: { name: string; filter: { [key: string]: unknown } }[],
    ): Observable<{ report_id: number; created_groups: HIPCustomGroupItem[] }> {
        const body: Record<string, unknown> = {
            is_new_report: isNewReport,
            report_name: reportName,
            data: reportData,
            filter,
            channels,
            is_preview: isPreview,
            ai_generated_groups: aiGeneratedGroups,
        };
        if (reportId !== null) {
            body['report_id'] = reportId;
        }
        return this.http.post<{ report_id: number; created_groups: HIPCustomGroupItem[] }>(
            UrlStore.api.lookupsV2.hipSaveWithAIGroups,
            body,
        );
    }

    patchReportName(screenName: string, reportId: number, reportName: string): Observable<{ success: boolean }> {
        const url = UrlStore.api.lookupsV2.savedReport.concat(`/${screenName}`);
        const body = {
            report_id: reportId,
            report_name: reportName,
        };
        return this.http.patch<{ success: boolean }>(url, body);
    }

    patchSavedReport(
        reportId: number,
        savedReportFields: {
            startDate: string;
            endDate: string;
            savedMetrics?: string[];
            savedMetricsOrder?: string[];
        },
    ): Observable<{ success: boolean }> {
        const url = UrlStore.api.lookupsV2.savedReport;
        const body = {
            reportId,
            ...savedReportFields,
        };
        return this.http.patch<{ success: boolean }>(url, body);
    }

    saveEmailReportFrequency(
        emailReportId: number,
        reportId: number,
        frequencyId: number,
        dow: number[],
        time: string,
        timezone: string,
        weekOfMonth: string[],
        firstDayOfMonth: boolean,
        userIds: number[],
        isActive: boolean,
        endDate: Moment,
        excludePastWeeks: boolean = false,
    ): Observable<number> {
        const url = UrlStore.api.lookupsV2.saveEmailReportFrequency;
        let body = {
            report_id: reportId,
            frequency_id: frequencyId,
            ...(emailReportId && { email_report_id: emailReportId }),
            is_active: isActive,
            end_date: endDate,
            exclude_past_weeks: excludePastWeeks,
        };
        if (isActive) {
            body = {
                report_id: reportId,
                frequency_id: frequencyId,
                ...(emailReportId && { email_report_id: emailReportId }),
                ...(dow && { dow }),
                ...(time && { time }),
                ...(timezone && { timezone }),
                ...(weekOfMonth && { week_of_month: weekOfMonth }),
                ...(firstDayOfMonth && { first_day_of_month: firstDayOfMonth }),
                ...(userIds && { user_ids: userIds }),
                is_active: isActive,
                end_date: endDate,
                exclude_past_weeks: excludePastWeeks,
            };
        }
        return this.http.put<number>(url, body);
    }

    sendOneTimeReport(reportId: number, userIds: number[]): Observable<void> {
        const url = UrlStore.api.lookupsV2.sendOneTimeReport;
        const body = {
            report_id: reportId,
            user_ids: userIds,
        };
        return this.http.put<void>(url, body);
    }

    saveEmailReportUsers(emailReportId: number, userIds: number[]): Observable<number> {
        const url = UrlStore.api.lookupsV2.saveEmailReportUsers;
        const body = {
            email_report_id: emailReportId,
            user_ids: userIds,
        };
        return this.http.patch<number>(url, body);
    }

    sendEmailReportMail(emailReportId: number): Observable<void> {
        const url = UrlStore.api.userAdmin.emailedReport;
        return this.http.post<void>(url, {
            emailReportID: emailReportId,
        });
    }

    getReportUsers(channelIds: number[]): Observable<ReportUser[]> {
        let url = UrlStore.api.lookupsV2.reportUsers;
        if (channelIds.length > 0) {
            url += `?channels=${channelIds}`;
        }
        return this.http.get<ReportUser[]>(url);
    }

    savePoliticalWindow(markets: number[], startDate: string, endDate: string, windowName: string): Observable<Grid<PoliticalWindowRow>> {
        return this.http.put<Grid<PoliticalWindowRow>>(UrlStore.api.admin.politicalWindows, {
            markets,
            startDate,
            endDate,
            windowName,
        });
    }

    toggleSaveReportIsFavorited(screenName: string, reportId: number, isFavorited: boolean): Observable<void> {
        const url = UrlStore.api.lookupsV2.savedReport.concat(`/${screenName}`);
        return this.http.post<void>(url, {
            report_id: reportId,
            is_favorited: isFavorited,
        });
    }

    deleteSavedReport(screenName: string, reportId: number, deleteForAll = false): Observable<void> {
        let url = UrlStore.api.lookupsV2.savedReport.concat(`/${screenName}?report_id=${reportId}`);
        if (deleteForAll) {
            url += '&delete_for_all=true';
        }
        return this.http.delete<never>(url);
    }

    deleteEmailSavedReport(screenName: string, emailReportId: number): Observable<void> {
        const url = UrlStore.api.lookupsV2.emailSavedReport.concat(`?screen_name=${screenName}&email_report_id=${emailReportId}`);
        return this.http.delete<never>(url);
    }

    userSaveReportAccess(screenName: string, reportsAccessData: ReportAccess[]): Observable<void> {
        const url = UrlStore.api.lookupsV2.userSavedReportAccess.concat(`/${screenName}`);
        return this.http.post<void>(url, { data: reportsAccessData });
    }

    loadSavedReports(screenName: string, tab: number): Observable<SavedReport[]> {
        const url = UrlStore.api.lookupsV2.savedReport.concat(`/${screenName}?tab=${tab}`);
        return this.http.get<SavedReport[]>(url);
    }

    loadExistingHighImpactReports(): Observable<Grid<ExistingReportRowData>> {
        const url = UrlStore.api.highImpactProgramReport.loadExistingHighImpactReports;
        return this.http.get<Grid<ExistingReportRowData>>(url);
    }

    getSavedReport(reportId: number): Observable<SavedReport[]> {
        const url = UrlStore.api.lookupsV2.savedReport.concat(`?reportId=${reportId}`);
        return this.http.get<SavedReport[]>(url);
    }

    getDefaultReportForRateCard(): Observable<SavedReport | null> {
        return this.http.get<SavedReport | null>(UrlStore.api.rateCardLookupsV2.defaultReport);
    }

    setDefaultReportForRateCard(reportId: number): Observable<{ reportId: number }> {
        return this.http.put<{ reportId: number }>(UrlStore.api.rateCardLookupsV2.defaultReport, { reportId });
    }

    removeDefaultReportForRateCard(): Observable<void> {
        return this.http.delete<void>(UrlStore.api.rateCardLookupsV2.defaultReport);
    }

    getSharedReport(reportId: string): Observable<SavedReport[]> {
        const url = UrlStore.api.lookupsV2.sharedReport.concat(`?sharedReportId=${reportId}`);
        return this.http.get<SavedReport[]>(url);
    }

    createSharedReport(reportId: number): Observable<SavedReport[]> {
        const url = UrlStore.api.lookupsV2.sharedReport.concat(`?reportId=${reportId}`);
        return this.http.post<SavedReport[]>(url, {});
    }

    getFilters(screenName: string): Observable<SavedFilter[]> {
        return this.http.get<SavedFilter[]>(UrlStore.api.lookupsV2.filters.concat(`/${screenName}`));
    }

    saveFilterToLocalStorage(filterName: string, filter: unknown): void {
        this.storage.setItem(filterName, JSON.stringify(filter));
    }

    getFilterFromLocalStorage(filterName: string): string[] {
        return this.safeParseJson(this.storage.getItem(filterName), []);
    }

    getTypedMetricsOrderFromLocalStorage(filterName: string): MetricsOrderTypedItem[] {
        return this.safeParseJson(this.storage.getItem(filterName), []);
    }

    getColumnSortOrderFromLocalStorage<T>(filterName: string): T {
        return this.safeParseJson<T>(this.storage.getItem(filterName), null as T);
    }

    getIgnoredDuplicatesFromLocalStorage(): string[] {
        return this.safeParseJson(this.storage.getItem(UrlStore.localStorageKeys.ignoredDuplicateSegments), []);
    }

    getCustomDaypartDOW(): DOWLookupItem[] {
        return [
            {
                name: 'M-F',
                mask: {
                    monday: true,
                    tuesday: true,
                    wednesday: true,
                    thursday: true,
                    friday: true,
                    saturday: false,
                    sunday: false,
                },
            },
            {
                name: 'Sa-Su',
                mask: {
                    monday: false,
                    tuesday: false,
                    wednesday: false,
                    thursday: false,
                    friday: false,
                    saturday: true,
                    sunday: true,
                },
            },
            {
                name: 'M-Su',
                mask: {
                    monday: true,
                    tuesday: true,
                    wednesday: true,
                    thursday: true,
                    friday: true,
                    saturday: true,
                    sunday: true,
                },
            },
        ];
    }

    /**
     * DOW options for the add-program/bulk-add popups - the backend already resolves
     * `ids` to the fixed Mon=0..Sun=6 day-index list and sorts chronologically. It
     * returns whatever real dow_group segments exist for the customer as-is (any
     * count), only falling back to the canonical 10 defaults when none exist at all.
     */
    getSegmentDOW(): Observable<SegmentDOWLookupItem[]> {
        return this.generateCachedGet<SegmentDOWLookupItem>(false, UrlStore.api.rateCardLookupsV2.dowSegments);
    }

    getCustomDaypartHours(): HourLookupItem[] {
        return [
            { id: 1, value: 0, displayValue: '12a', startTime: '00:00:00', endTime: '00:00:00' },
            { id: 2, value: 1, displayValue: '1a', startTime: '01:00:00', endTime: '01:00:00' },
            { id: 3, value: 2, displayValue: '2a', startTime: '02:00:00', endTime: '02:00:00' },
            { id: 4, value: 3, displayValue: '3a', startTime: '03:00:00', endTime: '03:00:00' },
            { id: 5, value: 4, displayValue: '4a', startTime: '04:00:00', endTime: '04:00:00' },
            { id: 6, value: 5, displayValue: '5a', startTime: '05:00:00', endTime: '05:00:00' },
            { id: 7, value: 6, displayValue: '6a', startTime: '06:00:00', endTime: '06:00:00' },
            { id: 8, value: 7, displayValue: '7a', startTime: '07:00:00', endTime: '07:00:00' },
            { id: 9, value: 8, displayValue: '8a', startTime: '08:00:00', endTime: '08:00:00' },
            { id: 10, value: 9, displayValue: '9a', startTime: '09:00:00', endTime: '09:00:00' },
            { id: 11, value: 10, displayValue: '10a', startTime: '10:00:00', endTime: '10:00:00' },
            { id: 12, value: 11, displayValue: '11a', startTime: '11:00:00', endTime: '11:00:00' },
            { id: 13, value: 12, displayValue: '12p', startTime: '12:00:00', endTime: '12:00:00' },
            { id: 14, value: 13, displayValue: '1p', startTime: '13:00:00', endTime: '13:00:00' },
            { id: 15, value: 14, displayValue: '2p', startTime: '14:00:00', endTime: '14:00:00' },
            { id: 16, value: 15, displayValue: '3p', startTime: '15:00:00', endTime: '15:00:00' },
            { id: 17, value: 16, displayValue: '4p', startTime: '16:00:00', endTime: '16:00:00' },
            { id: 18, value: 17, displayValue: '5p', startTime: '17:00:00', endTime: '17:00:00' },
            { id: 19, value: 18, displayValue: '6p', startTime: '18:00:00', endTime: '18:00:00' },
            { id: 20, value: 19, displayValue: '7p', startTime: '19:00:00', endTime: '19:00:00' },
            { id: 21, value: 20, displayValue: '8p', startTime: '20:00:00', endTime: '20:00:00' },
            { id: 22, value: 21, displayValue: '9p', startTime: '21:00:00', endTime: '21:00:00' },
            { id: 23, value: 22, displayValue: '10p', startTime: '22:00:00', endTime: '22:00:00' },
            { id: 24, value: 23, displayValue: '11p', startTime: '23:00:00', endTime: '23:00:00' },
        ];
    }

    getTimezones(clearCache = false): Observable<string[]> {
        return this.generateCachedGet<string>(clearCache, UrlStore.api.lookupsV2.timezones);
    }

    getRateExpirationDate(channelIds: number[]): Observable<Grid<RateExpirationDate>> {
        let url = UrlStore.api.lookupsV2.rateExpirationDate;
        if (channelIds.length > 0) {
            url += `?channels=${channelIds}`;
        }
        return this.generateCachedGetNoArray<Grid<RateExpirationDate>>(false, url);
    }

    getHipEarliestDataDate(): Observable<Moment | null> {
        return this.generateCachedGetNoArray<{ earliestDate: string | null }>(
            false,
            UrlStore.api.highImpactProgramReport.earliestDataDate,
        ).pipe(map((response) => (response?.earliestDate ? moment(response.earliestDate) : null)));
    }

    getLogos(color: 'white' | 'black' | 'sassblue' | 'analyticsblue' = 'black'): Observable<{
        raLogo: Blob;
        productLogo: Blob;
    }> {
        const raLogo = this.http.get(`/assets/images/ra-logo-${color}.png`, { responseType: 'blob' });
        const productLogo = this.http.get(`/assets/images/rate-optics-logo-${color}.png`, { responseType: 'blob' });
        return forkJoin({ raLogo, productLogo }).pipe(map((resp) => resp));
    }

    getCustomerLogo(): Observable<Blob> {
        return this.http
            .get<string>(UrlStore.api.lookupsV2.customerLogo)
            .pipe(switchMap((url: string) => new HttpClient(this.httpBackend).get(url, { responseType: 'blob' })));
    }

    getLogo(color: 'white' | 'black' | 'sassblue' | 'analyticsblue' = 'black'): Observable<Logo> {
        const img = new Image();
        img.src = `assets/images/rate-optics-logo-${color}.png`;
        return of({ image: img, height: color === 'black' ? 270 : 974, width: color === 'black' ? 1366 : 6280 });
    }

    autoPublish(channelIds: number[], autoPublish: boolean): Observable<unknown> {
        return this.http.post(UrlStore.api.lookupsV2.autoPublish, { channelIds, autoPublish });
    }

    getSystemSegments(
        channelId: number,
        segmentId: number,
        segmentType: string,
        baseSegmentationId: number,
        isPolitical = false,
    ): Observable<SystemSegment> {
        return this.http.get<SystemSegment>(
            UrlStore.api.rateCardLookupsV2.systemSegments +
                `?channelId=${channelId}&segmentId=${segmentId}&segmentType=${segmentType}&baseSegmentationId=${baseSegmentationId}&isPolitical=${isPolitical}`,
        );
    }

    saveSystemSegmentEdits(
        edits: {
            channelId: number;
            baseSegmentationId: number;
            daypartId: number;
            spotTypeId: number;
            dynamicSegmentation: { [key: string]: string };
            segmentId: number;
            segmentType: string;
            segmentName: string;
        }[],
        daypartOverride?: {
            channelId: number;
            baseSegmentationId: number;
            daypartId: number;
            spotTypeId: number;
            dynamicSegmentation: { [key: string]: string };
            overrideDaypartId: number;
        },
        editOnDynamicRateCard = true,
        isPolitical = false,
    ): Observable<EditSegmentNameParams[]> {
        const payload: {
            edits: typeof edits;
            daypartOverride?: typeof daypartOverride;
            editOnDynamicRateCard: boolean;
            isPolitical: boolean;
        } = { edits, editOnDynamicRateCard, isPolitical };
        if (daypartOverride) {
            payload.daypartOverride = daypartOverride;
        }
        return this.http.post<EditSegmentNameParams[]>(UrlStore.api.rateCardLookupsV2.systemSegments, payload);
    }

    getSystemSegmentAiredWeeks(
        channelId: number,
        baseSegmentationId: number,
        startDate: string,
        endDate: string,
    ): Observable<HiatusWeekSelection[]> {
        return this.http.post<HiatusWeekSelection[]>(UrlStore.api.rateCardLookupsV2.systemSegmentsAiredWeeks, {
            channelId,
            baseSegmentationId,
            startDate,
            endDate,
        });
    }

    saveSystemSegmentAiredWeeks(
        channelId: number,
        baseSegmentationId: number,
        airedWeeks: string[],
        startDate: string,
        endDate: string,
        reloadParams: { startDate: string; endDate: string; period: number },
        rateCardTab: number,
    ): Observable<RateCardMetricRowV2[]> {
        return this.http
            .patch<DynamicRateCardApiGrid<RateCardMetricRowV2, RateCardMetricAlertCalculations[]>>(
                UrlStore.api.rateCardLookupsV2.systemSegmentsAiredWeeks,
                {
                    channelId,
                    baseSegmentationId,
                    startDate,
                    endDate,
                    airedWeeks,
                    reloadParams,
                    rateCardTab,
                },
            )
            .pipe(
                switchMap((response: DynamicRateCardApiGrid<RateCardMetricRowV2, RateCardMetricAlertCalculations[]>) =>
                    calculateRateCardAlerts2<RateCardPricingTier, object, DynamicRateCardMetricValueV2>(
                        response.data as RateCardMetricRowV2<DynamicRateCardMetricValueV2>[],
                        response.alerts,
                        response.groupedColumns,
                        response.primaryTierMap,
                        response.pcodePrimaryTierMap,
                        response.tiers,
                        // ts-expect-error type mismatch
                        dynamicRateCardCopyCell,
                        {},
                    ).pipe(switchMap((data) => of(data.data))),
                ),
            );
    }

    public getSegments(
        channelIds: number[],
        daypartIds: number[],
        segmentation: Segmentation,
        filterValues: { [k: string]: (string | number)[] },
        clearCache: boolean,
        baseUrl: string,
    ): Observable<SegmentItem[]> {
        const body: Record<string, unknown> = {
            segment_type: segmentation.segment_type,
            channelIds,
        };
        if (daypartIds?.length) {
            body.dayparts = daypartIds;
        }
        if (Object.keys(filterValues).length > 0) {
            const dynamicFilters: { [k: string]: string } = {};
            Object.keys(filterValues).forEach((k) => {
                // Defensive: callers (e.g. generateSingleSelectFiltersWithSideText) sometimes pass
                // through undefined or non-array values when a parent filter hasn't been resolved
                // yet. Skip those entries instead of crashing on .join(undefined).
                const value = filterValues[k];
                if (Array.isArray(value) && value.length > 0 && value[0] !== undefined && value[0] !== null) {
                    dynamicFilters[k] = value.join(',');
                }
            });
            if (Object.keys(dynamicFilters).length > 0) {
                body.dynamic_filters = JSON.stringify(dynamicFilters);
            }
        }

        const cacheKey = `${baseUrl}:POST:${JSON.stringify(body)}`;
        if (clearCache) {
            this.cacheData[cacheKey] = null;
        }
        if (!this.cacheData[cacheKey]) {
            this.cacheData[cacheKey] = this.http.post<Segment[]>(baseUrl, body).pipe(shareReplay(1));
        }

        return (this.cacheData[cacheKey] as Observable<Segment[]>).pipe(
            map((segments) =>
                segments.map((s) => ({
                    id: s.segment_id,
                    ids: s.segment_ids,
                    name: s.segment_display_name ?? s.segment_name,
                })),
            ),
        );
    }

    public clearSegmentCache(): void {
        Object.keys(this.cacheData).forEach((key) => {
            if (key.includes('/segments')) {
                delete this.cacheData[key];
            }
        });
    }

    public getScenarioSegments(
        channelIds: number[],
        revenueCategoryIds: number[],
        segmentation: string,
        clearCache: boolean,
        baseUrl: string,
    ): Observable<SegmentItem[]> {
        const body: Record<string, unknown> = {
            segment_type: segmentation,
            channelIds,
        };
        if (revenueCategoryIds?.length) {
            body.revenueCategoryIds = revenueCategoryIds;
        }

        const cacheKey = `${baseUrl}:POST:${JSON.stringify(body)}`;
        if (clearCache) {
            this.cacheData[cacheKey] = null;
        }
        if (!this.cacheData[cacheKey]) {
            this.cacheData[cacheKey] = this.http.post<Segment[]>(baseUrl, body).pipe(shareReplay(1));
        }

        return (this.cacheData[cacheKey] as Observable<Segment[]>).pipe(
            map((segments) =>
                segments.map((s) => ({
                    id: s.segment_id,
                    ids: s.segment_ids,
                    name: s.segment_display_name ?? s.segment_name,
                })),
            ),
        );
    }

    public getSpotLengths(clearCache: boolean, baseUrl: string): Observable<LookupItem[]> {
        return this.generateCachedGet<LookupItem>(clearCache, baseUrl);
    }

    public getProducts(channels: number[], productTypeIds: number[] = null, clearCache = false): Observable<LookupItem[]> {
        const callUrl = productTypeIds
            ? UrlStore.api.lookupsV2.products.concat(`?productTypes=${productTypeIds.join(',')}&channels=${channels.join(',')}`)
            : UrlStore.api.lookupsV2.products.concat(`?channels=${channels.join(',')}`);
        return this.generateCachedGet<ChannelItem>(clearCache, callUrl);
    }

    public getProductTypes(channelIds: number[], clearCache = false): Observable<LookupItem[]> {
        let url = UrlStore.api.lookupsV2.productTypes;
        url += `?channels=${channelIds}`;
        return this.generateCachedGet<ChannelItem>(clearCache, url);
    }

    getStationExceptions(): Observable<Grid<StationExceptionRow>> {
        return this.http
            .get<Grid<StationExceptionRow>>(UrlStore.api.lookupsV2.stationExceptions)
            .pipe(switchMap(this.mapStationExceptionGrid));
    }

    getDigitalPricingTiers(clearCache: boolean = false): Observable<LookupItem[]> {
        return this.generateCachedGet<LookupItem>(clearCache, UrlStore.api.lookupsV2.digitalPricingTiers);
    }

    getDynamicPricingTiers(clearCache: boolean = false): Observable<LookupItem[]> {
        return this.generateCachedGet<LookupItem>(clearCache, UrlStore.api.lookupsV2.dynamicPricingTiers);
    }

    saveCustomGroup(groupName: string, filter: unknown): Observable<HIPCustomGroupItem[]> {
        return this.http.post<HIPCustomGroupItem[]>(UrlStore.api.lookupsV2.hipCustomGroups, { name: groupName, filter });
    }

    getHipCustomGroups(options?: { source?: string; reportId?: number }): Observable<HIPCustomGroupItem[]> {
        const params: { [k: string]: string } = {};
        if (options?.source) params['source'] = options.source;
        if (options?.reportId !== undefined) params['reportId'] = String(options.reportId);
        return this.http.get<HIPCustomGroupItem[]>(UrlStore.api.lookupsV2.hipCustomGroups, { params });
    }

    updateCustomGroup(groupId: number, groupName: string, filter: unknown): Observable<HIPCustomGroupItem> {
        return this.http.put<HIPCustomGroupItem>(`${UrlStore.api.lookupsV2.hipCustomGroups}`, { id: groupId, name: groupName, filter });
    }

    deleteCustomGroup(groupId: number): Observable<void> {
        return this.http.delete<void>(`${UrlStore.api.lookupsV2.hipCustomGroups}/${groupId}`);
    }

    private safeParseJson<T>(value: string | null, defaultValue: T): T {
        if (value === null || value === undefined || value === 'undefined') {
            return defaultValue;
        }
        try {
            return JSON.parse(value) ?? defaultValue;
        } catch {
            return defaultValue;
        }
    }

    private mapStationExceptionGrid(grid: Grid<StationExceptionRow>): Observable<Grid<StationExceptionRow>> {
        const mappedGrid: Grid<StationExceptionRow> = {
            columns: grid.columns,
            data: grid.data.map((row) => ({
                ...row,
            })),
        };
        return of(mappedGrid);
    }
}
